import { FilesUploader } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { DirectUploadMedia } from "libs/directUpload";
import { CompanySection } from "modules/auth";
import { CompanyManager } from "modules/companyAdminAuth";
import { useCreateManager, useUpdateManager } from "modules/companyAdminAuth";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Checkbox, FormField, Input, MediaFilePicker, Modal } from "ui";

import styles from "./NewManagerModal.module.scss";

const FORM_ID = "NEW_MANAGER_FORM";

const SECTION_OPTIONS: Array<{ value: CompanySection; label: string }> = [
  { value: CompanySection.Dashboard, label: "Dashboard" },
  { value: CompanySection.Orders, label: "Orders" },
  { value: CompanySection.Drivers, label: "Drivers" },
  { value: CompanySection.Financial, label: "Financial" },
  { value: CompanySection.StaffManagement, label: "Staff Management" },
  { value: CompanySection.CompanySettings, label: "Company Settings" }
];

interface FormValues {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  repeatPassword: string;
  avatarMediaId: string | null;
  allowedSections: CompanySection[];
}

interface NewManagerModalProps {
  isOpen: boolean;
  editableManager?: CompanyManager | null;
  onClose: () => void;
}

const NewManagerModal = ({
  isOpen,
  editableManager,
  onClose
}: NewManagerModalProps) => {
  const { t } = useTranslation();
  const isEdit = !!editableManager;

  const initialAvatar: DirectUploadMedia | null = editableManager?.avatar
    ? (editableManager.avatar as unknown as DirectUploadMedia)
    : null;

  const [avatarMedia, setAvatarMedia] = useState<DirectUploadMedia | null>(
    initialAvatar
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      repeatPassword: "",
      avatarMediaId: null,
      allowedSections: []
    }
  });

  const passwordValue = watch("password");
  const allowedSections = watch("allowedSections");

  useEffect(() => {
    if (isOpen) {
      reset({
        firstname: editableManager?.firstname ?? "",
        lastname: editableManager?.lastname ?? "",
        email: editableManager?.email ?? "",
        password: "",
        repeatPassword: "",
        avatarMediaId: editableManager?.avatar?.id ?? null,
        allowedSections: editableManager?.allowedSections ?? []
      });
      setAvatarMedia(initialAvatar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editableManager?.id]);

  const closeAndReset = () => {
    onClose();
  };

  const { mutate: create, isLoading: isCreating } = useCreateManager({
    onSuccess: () => {
      toast.success(t("Manager created"));
      closeAndReset();
    },
    onError: (e) => toast.error(e?.message || t("Failed to create"))
  });

  const { mutate: update, isLoading: isUpdating } = useUpdateManager({
    onSuccess: () => {
      toast.success(t("Manager updated"));
      closeAndReset();
    },
    onError: (e) => toast.error(e?.message || t("Failed to update"))
  });

  const onAvatarUploaded = (media: DirectUploadMedia) => {
    setAvatarMedia(media);
    setValue("avatarMediaId", media.id);
  };

  const onAvatarRemoved = () => {
    setAvatarMedia(null);
    setValue("avatarMediaId", null);
  };

  const toggleSection = (section: CompanySection) => {
    const current = allowedSections ?? [];
    const next = current.includes(section)
      ? current.filter((s) => s !== section)
      : [...current, section];
    setValue("allowedSections", next, { shouldValidate: true });
  };

  const onSubmit = (values: FormValues) => {
    if (isEdit && editableManager) {
      update({
        id: editableManager.id,
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        password: values.password || undefined,
        avatarMediaId: values.avatarMediaId,
        allowedSections: values.allowedSections
      });
    } else {
      create({
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        password: values.password,
        avatarMediaId: values.avatarMediaId ?? undefined,
        allowedSections: values.allowedSections
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t("Edit Manager") : t("New Manager")}
      formId={FORM_ID}
      okText={isEdit ? t("Save changes") : t("Create")}
      cancelText={t("Cancel")}
      isLoading={isCreating || isUpdating}
      isOkDisabled={!allowedSections?.length}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.form__avatarBlock}>
          <FilesUploader
            onSuccessUploading={onAvatarUploaded}
            uploadDirectoryKey={MediaDirectoryKeys.DeliveryCompany}
          >
            {(uploaderProps) => (
              <MediaFilePicker
                allowedMediaTypes={[MediaType.IMAGE]}
                size="md"
                variant="circle"
                customSize={{ width: 80, height: 80 }}
                {...uploaderProps}
                value={avatarMedia}
                onDeleteMediaFile={onAvatarRemoved}
              />
            )}
          </FilesUploader>
          <span className={styles.form__avatarHint}>{t("Change photo")}</span>
        </div>

        <div className={styles.form__row}>
          <FormField
            label={t("First Name")}
            errorMessage={errors.firstname?.message}
          >
            <Input
              {...register("firstname", { required: t("Required field") })}
              placeholder="James"
            />
          </FormField>

          <FormField
            label={t("Last Name")}
            errorMessage={errors.lastname?.message}
          >
            <Input
              {...register("lastname", { required: t("Required field") })}
              placeholder="Peterson"
            />
          </FormField>
        </div>

        <FormField label={t("Email")} errorMessage={errors.email?.message}>
          <Input
            type="email"
            {...register("email", { required: t("Required field") })}
            placeholder="manager@example.com"
          />
        </FormField>

        <div className={styles.form__row}>
          <FormField
            label={t("Password")}
            errorMessage={errors.password?.message}
          >
            <Input
              type="password"
              isPassword
              {...register("password", {
                required: !isEdit && t("Required field"),
                minLength: { value: 8, message: t("Min 8 characters") }
              })}
              placeholder="••••••••"
            />
          </FormField>

          <FormField
            label={t("Repeat Password")}
            errorMessage={errors.repeatPassword?.message}
          >
            <Input
              type="password"
              isPassword
              {...register("repeatPassword", {
                validate: (value) =>
                  value === passwordValue || t("Passwords do not match")
              })}
              placeholder="••••••••"
            />
          </FormField>
        </div>

        <div className={styles.form__sections}>
          <div className={styles.form__sectionsLabel}>
            {t("Select available sections")}
          </div>
          {SECTION_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.form__sectionRow}>
              <Checkbox
                checked={allowedSections?.includes(opt.value) ?? false}
                onChange={() => toggleSection(opt.value)}
              />
              <span>{t(opt.label)}</span>
            </label>
          ))}
        </div>
      </form>
    </Modal>
  );
};

export default NewManagerModal;
